import * as R from 'ramda';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import React, { useMemo } from 'react';
import upperCase from 'voca/upper_case';
import {
  pure,
  compose,
  lifecycle,
  withProps,
  withState,
  withHandlers,
} from 'react-recompose';
// components
import { ConfirmComponent } from '../../../components/confirm';
import { ChargeFormFooter } from '../../../components/form-footer';
import PriceSheetItems from '../../../components/price-sheet-items';
import { ChargesTotal } from '../../../components/charge/components';
import { getAccessorialsForState } from '../../../components/charge/helpers';
import { withRecalculateAllChargesId } from '../../../components/charge/hocs';
import { DMChargeComponent } from '../../../components/charge/formik/charges-array';
import {
  withAsyncChargeAccessorials,
  getTelSharedAccessorialsApplyTo,
} from '../../../components/charge/hocs/with-async-charge-accessorials';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import {
  withAsyncConfigs,
  withAsyncBranchConfigsByNames,
  withAsyncMarginViolationRules,
  withConnectModalAndLoaderActions,
  withComponentDidUpdatePropCallback,
  withAsyncGetUserGeneralListFullNameOnDidMount,
} from '../../../hocs';
// ui
import { Box, StickedBox, SectionsDivider } from '../../../ui';
// feature rate
import RateFleetMap from './fleet-map';
import RateHeader from './rate-header';
import { getDriverRateValidationSchema } from '../validation';
import { withFleetAssignment } from '../hooks/with-fleet-assigment';
import { withSearchLocation, withAsyncDeadheadMiles } from '../hocs';
import MarginViolationSection from '../components/margin-violation-section';
import { withPreselectedTrailers } from '../hooks/with-preselected-trailers';
import { NotAllowedCustomerRate } from '../components/not-allowed-customer-rate';
import { getTeamDriverCompensations, getDriverCompensationsReqBody } from '../api';
import {
  DriverSection,
  PointsSection,
  EditDriverSection,
  GeneralSectionForTel,
} from '../sections';
import {
  sortTruckOptions,
  getDriverInitCharges,
  getChargesFromValues,
  filterDriversOptions,
  getMarginViolationData,
  mapDispatchersToOptions,
  getDeletedDriversOptions,
  setDefaultDriverRateValues,
  getChargesArrayNameFromProps,
  makeResultObjectOnChangeDriver,
  makeResultObjectOnRemoveDriver,
  makeResultObjectOnChangeVendorRate,
} from '../helpers';
//////////////////////////////////////////////////

const getTelBranchGuid = (props) => R.or(
  R.or(props.branchGuid, R.path([GC.FIELD_TEL, GC.FIELD_BRANCH_GUID], props)),
  R.path([GC.FIELD_TEL, GC.FIELD_BRANCH, GC.FIELD_GUID], props),
);

const handleGetSingleDriverCompensation = async (driverGuid, props) => {
  const { getSingleDriverCompensations } = props;

  const reqBody = getDriverCompensationsReqBody(driverGuid, props);

  const driver = await getSingleDriverCompensations(reqBody);

  return driver;
};

const handleGetVendorRates = async (truckGuid, props) => {
  const { values, telGuid, loadData, telEvents, isFromOrder, isFromPlanner, getVendorRates } = props;

  const { serviceType, transportationMode } = values;

  const branchGuid = getTelBranchGuid(props);

  if (G.isAllTrue(
    G.isTrue(isFromPlanner),
    G.isNotNilAndNotEmpty(truckGuid),
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(telEvents),
  )) {
    const data = await getVendorRates({
      telGuid,
      serviceType,
      loadGuid: telGuid,
      transportationMode,
      truckGuids: R.of(Array, truckGuid),
      loadData: {
        events: telEvents,
      },
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    });

    return data;
  }

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(truckGuid),
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(telGuid),
  )) {
    const data = await getVendorRates({
      telGuid,
      serviceType,
      loadGuid: telGuid,
      transportationMode,
      truckGuids: R.of(Array, truckGuid),
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    });

    return data;
  }

  if (G.isAllTrue(
    G.isTrue(isFromOrder),
    G.isNotNilAndNotEmpty(loadData),
    G.isNotNilAndNotEmpty(truckGuid),
    G.isNotNilAndNotEmpty(branchGuid),
  )) {
    const data = await getVendorRates({
      loadData,
      serviceType,
      transportationMode,
      truckGuids: R.of(Array, truckGuid),
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    });

    return data;
  }
};

const handleGetAllVendorRates = async (props) => {
  const { values, telGuid, loadData, telEvents, isFromOrder, isFromPlanner, getVendorRates } = props;

  const { truckGuid, serviceType, transportationMode } = values;

  const branchGuid = getTelBranchGuid(props);

  if (G.isAllTrue(
    G.isTrue(isFromPlanner),
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(telEvents),
  )) {
    const data = await getVendorRates({
      telGuid,
      serviceType,
      loadGuid: telGuid,
      transportationMode,
      loadData: {
        events: telEvents,
      },
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    });

    return data;
  }

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(telGuid),
  )) {
    const data = await getVendorRates({
      telGuid,
      serviceType,
      loadGuid: telGuid,
      transportationMode,
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    });

    return data;
  }

  if (G.isAllTrue(
    G.isTrue(isFromOrder),
    G.isNotNilAndNotEmpty(loadData),
    G.isNotNilAndNotEmpty(truckGuid),
    G.isNotNilAndNotEmpty(branchGuid),
  )) {
    const data = await getVendorRates({
      loadData,
      serviceType,
      transportationMode,
      truckGuids: R.of(Array, truckGuid),
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    });

    return data;
  }
};

const handleGetAssignmentNew = (props, shouldGetCompensations) => {
  const { values, telGuid, ignoreHazmat, getAssignmentByLoadData } = props;

  const branchGuid = getTelBranchGuid(props);

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(telGuid),
  )) {
    const { serviceType, customerTotal, transportationMode } = values;

    let data = {
      telGuid,
      serviceType,
      customerTotal,
      loadGuid: telGuid,
      transportationMode,
      shouldGetCompensations,
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    };

    if (G.isBoolean(ignoreHazmat)) data = R.assoc('ignoreHazmatEndorsement', ignoreHazmat, data);

    getAssignmentByLoadData(data);
  }
};

const handleGetAssignmentFromRouteBuilder = (props, shouldGetCompensations) => {
  const { values, telGuid, telEvents, ignoreHazmat, getAssignmentFromRouteBuilder } = props;

  const branchGuid = getTelBranchGuid(props);

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(telEvents),
  )) {
    const { serviceType, customerTotal, transportationMode } = values;

    let data = {
      telGuid,
      serviceType,
      customerTotal,
      loadGuid: telGuid,
      transportationMode,
      shouldGetCompensations,
      loadData: {
        events: telEvents,
      },
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    };

    if (G.isBoolean(ignoreHazmat)) data = R.assoc('ignoreHazmatEndorsement', ignoreHazmat, data);

    getAssignmentFromRouteBuilder(data);
  }
};

const handleGetAssignmentFromOrder = (props, shouldGetCompensations) => {
  const { values, loadData, ignoreHazmat, getAssignmentFromOrder } = props;

  const branchGuid = getTelBranchGuid(props);

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(branchGuid),
    G.isNotNilAndNotEmpty(loadData),
  )) {
    const { serviceType, transportationMode } = values;

    let data = {
      loadData,
      serviceType,
      transportationMode,
      shouldGetCompensations,
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    };

    if (G.isBoolean(ignoreHazmat)) data = R.assoc('ignoreHazmatEndorsement', ignoreHazmat, data);

    getAssignmentFromOrder(data);
  }
};

const getSecondaryDriver = (secondaryDriverGuid, rateData, availableDrivers) => {
  const driverFromRateData = R.path([secondaryDriverGuid], rateData);
  const driverFromAvailableDrivers = R.path([secondaryDriverGuid], availableDrivers);

  if (G.isNotNilAndNotEmpty(driverFromRateData)) return driverFromRateData;

  if (G.isNotNilAndNotEmpty(driverFromAvailableDrivers)) return driverFromAvailableDrivers;

  return null;
};

const getStartLocation = (driver) => {
  const precedingLocation = R.path(['precedingLocation'], driver);
  const currentLocation = R.path(['currentLocation'], driver);

  if (G.isNotNilAndNotEmpty(precedingLocation)) {
    const sourceName = R.path(['telPrimaryReferenceValue'], precedingLocation);

    const location = {
      ...precedingLocation,
      sourceName,
      sourceType: GC.START_POINT_SOURCE_TYPE_TEL,
    };

    const current = R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR, location);

    return R.assoc(
      GC.FIELD_ADDRESS_1,
      G.concatLocationFields(current),
      current,
    );
  }

  if (G.isNotNilAndNotEmpty(currentLocation)) {
    const sourceName = R.path([GC.FIELD_CREATED_BY], currentLocation);

    const location = {
      ...currentLocation,
      sourceName,
      sourceType: GC.START_POINT_SOURCE_TYPE_LNL,
    };

    const current = R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR, location);

    return R.assoc(
      GC.FIELD_ADDRESS_1,
      G.concatLocationFields(current),
      current,
    );
  }
};

const getRateDriverData = (driver, values, isUpdate) => {
  const truckGuid = isUpdate
  ? G.getPropFromObject(GC.FIELD_TRUCK_GUID, values)
  : R.path(['assignment', GC.FIELD_TRUCK_GUID], driver);

  const secondaryDriverGuid = isUpdate
    ? G.getPropFromObject(GC.FIELD_SECONDARY_DRIVER_GUID, values)
    : R.path(['assignment', GC.FIELD_SECONDARY_DRIVER_GUID], driver);

  const primaryDistanceToStart = R.propOr(0, GC.FIELD_DISTANCE_TO_START, driver);

  const startLocation = getStartLocation(driver);

  return {
    truckGuid,
    startLocation,
    secondaryDriverGuid,
    primaryDistanceToStart,
  };
};

const handleCheckChargesBeforeSubmit = ({ values, openModal, closeModal, submitAction }) => {
  const { currency } = values;

  const chargeNames = [
    GC.FIELD_FLEET_VENDOR_CHARGES,
    GC.FIELD_PRIMARY_DRIVER_CHARGES,
    GC.FIELD_SECONDARY_DRIVER_CHARGES,
  ];

  const totals = R.map(
    (chargeName) => G.getCalcTelRateChargesTotalPay(R.prop(chargeName, values), currency),
    chargeNames,
  );

  if (R.all(G.isZero, totals)) {
    const locale1 = G.getWindowLocale('messages:driver-rate-is-zero-1', 'The Driver/Vendor rate is');
    const locale2 = G.getWindowLocale('messages:double-check-or-continue', 'Please double check or continue.');
    const textLocale = `${locale1} ${G.getCurrencySymbol(currency)}0. ${locale2}`;

    const component = <ConfirmComponent textLocale={textLocale} />;

    const modal = {
      component,
      options: {
        width: 600,
        controlButtons: [
          {
            type: 'button',
            name: G.getWindowLocale('actions:submit', 'Submit'),
            action: () => {
              closeModal();
              submitAction();
            },
          },
        ],
      },
    };

    return openModal(modal);
  }

  submitAction();
};

const enhanceDrivers = compose(
  withState('truckFleetVendor', 'setTruckFleetVendor', null),
  withState('rateCustomerTotal', 'setRateCustomerTotal', null),
  withState('driverFleetVendor', 'setDriverFleetVendor', null),
  withState('vendorRateCustomerTotal', 'setVendorRateCustomerTotal', null),
  withState('primaryDriverPayedHours', 'setPrimaryDriverPayedHours', null),
  withState('secondaryDriverPayedHours', 'setSecondaryDriverPayedHours', null),
  withState('primaryDriverDistanceToStart', 'setPrimaryDriverDistanceToStart', 0),
  withState('secondaryDriverDistanceToStart', 'setSecondaryDriverDistanceToStart', 0),
  withHandlers({
    handleChangeDriversSelect: (props) => async (
      driverGuid,
      fieldId,
      from,
      isUpdate = false,
    ) => {
      const {
        values,
        rateData,
        setValues,
        vendorRates,
        activeDriver,
        initialValues,
        setFieldValue,
        setActiveDriver,
        setTruckFleetVendor,
        setRateCustomerTotal,
        availableDriversData,
        vendorRateCustomerTotal,
        sharedAccessorialsApplyTo,
        setVendorRateCustomerTotal,
        setPrimaryDriverPayedHours,
        setSecondaryDriverPayedHours,
        setPrimaryDriverDistanceToStart,
        handleSetRecalculateAllChargesId,
        setSecondaryDriverDistanceToStart,
      } = props;

      const availableDrivers = R.indexBy(
        R.prop(GC.FIELD_GUID),
        R.pathOr([], ['drivers'], availableDriversData),
      );

      const driver = R.or(
        R.path([driverGuid], rateData),
        R.path([driverGuid], availableDrivers),
      );

      const initialCustomerTotal = R.prop(GC.FIELD_CUSTOMER_TOTAL, initialValues);

      const cloRateAdjustments = R.path([GC.FIELD_CLO_RATE_ADJUSTMENTS], availableDriversData);

      if (R.equals(GC.FIELD_PRIMARY_DRIVER_GUID, fieldId)) {
        if (R.isNil(driverGuid)) {
          const result = makeResultObjectOnRemoveDriver();

          const customerTotal = initialCustomerTotal;

          const newValues = R.mergeRight({ ...values, customerTotal }, result);

          setActiveDriver('primary');
          setRateCustomerTotal(null);
          setVendorRateCustomerTotal(null);

          return setValues(newValues);
        }

        const {
          truckGuid,
          startLocation,
          secondaryDriverGuid,
          primaryDistanceToStart,
        } = getRateDriverData(driver, values, isUpdate);

        const secondaryDriver = getSecondaryDriver(secondaryDriverGuid, rateData, availableDrivers);

        const availTrucks = R.prop('availTrucks', props);
        const truck = R.find(R.propEq(truckGuid, GC.FIELD_GUID), availTrucks);
        const truckFleetVendor = R.path([GC.SYSTEM_OBJECT_FLEET_VENDOR], truck);

        const defaultUomSystem = G.getConfigGeneralUomCalcDefaultUomSystemFromWindow();
        const deadheadUom = R.pathOr(GC.UOM_MILE, [defaultUomSystem], GC.uomSystemToDistanceUomMap);

        const deadhead = G.milesKmsFromToAccordingSystemWithNullable(
          deadheadUom,
          GC.UOM_KILOMETER,
          primaryDistanceToStart,
          2,
        );

        let result = makeResultObjectOnChangeDriver(driver, secondaryDriver, props, isUpdate);

        const currentRateCustomerTotal = R.path([GC.FIELD_PRICE, GC.FIELD_CUSTOMER_TOTAL], driver);

        setRateCustomerTotal(currentRateCustomerTotal);

        let currentVendorRateCustomerTotal = null;

        if (G.notEquals(from, 'newCompensation')) {
          handleGetSingleDriverCompensation(driverGuid, props);

          if (R.and(G.isNotNilAndNotEmpty(truckFleetVendor), G.isNotNilAndNotEmpty(vendorRates))) {
            const propsObj = { vendorRates, truckFleetVendor, values: result };

            const vendorResult = makeResultObjectOnChangeVendorRate(propsObj);

            const customerTotal = R.prop(GC.FIELD_CUSTOMER_TOTAL, vendorResult);

            if (G.isNotNilAndNotEmpty(customerTotal)) {
              setVendorRateCustomerTotal(customerTotal);

              currentVendorRateCustomerTotal = customerTotal;
            }

            result = G.mergeWithoutOneFieldNullUndefinedOrEmptyString(GC.FIELD_CUSTOMER_TOTAL, result, vendorResult);
          }
        }

        const newValues = R.mergeRight(values, result);

        let valuesToUse = {
          ...newValues,
          frontData: {
            driver,
            secondaryDriver,
            vendor: truckFleetVendor,
          },
          [GC.FIELD_DEADHEAD_DISTANCE]: deadhead,
          [GC.FIELD_DEADHEAD_DISTANCE_UOM]: deadheadUom,
          [GC.FIELD_START_POINT_LOCATION]: startLocation,
          [GC.FIELD_CLO_RATE_ADJUSTMENTS]: cloRateAdjustments,
        };

        if (R.and(R.equals(from, 'newCompensation'), G.isNotNilAndNotEmpty(secondaryDriverGuid))) {
          const resData = await getTeamDriverCompensations(secondaryDriverGuid, props);

          const charges = R.path(['rateData', 0, GC.FIELD_CHARGES], resData);

          valuesToUse = R.assoc(GC.FIELD_SECONDARY_DRIVER_CHARGES, getDriverInitCharges(charges, props), valuesToUse);
        }

        if (R.and(R.equals(from, 'newCompensation'), G.isNotNilAndNotEmpty(truckFleetVendor))) {
          const vendorRates = await handleGetVendorRates(truckGuid, props);

          if (G.isNotNilAndNotEmpty(vendorRates)) {
            const propsObj = { vendorRates, truckFleetVendor, values: valuesToUse, sharedAccessorialsApplyTo };

            const result = makeResultObjectOnChangeVendorRate(propsObj);

            const customerTotal = R.prop(GC.FIELD_CUSTOMER_TOTAL, result);

            if (G.isNotNilAndNotEmpty(customerTotal)) {
              setVendorRateCustomerTotal(customerTotal);

              currentVendorRateCustomerTotal = customerTotal;
            }

            valuesToUse = G.mergeWithoutOneFieldNullUndefinedOrEmptyString(
              GC.FIELD_CUSTOMER_TOTAL,
              valuesToUse,
              result,
            );
          }
        }

        if (G.isAllTrue(
          G.isNotNilAndNotEmpty(currentRateCustomerTotal),
          G.isNilOrEmpty(vendorRateCustomerTotal),
          G.isNilOrEmpty(currentVendorRateCustomerTotal),
        )) {
          valuesToUse = R.assoc(GC.FIELD_CUSTOMER_TOTAL, currentRateCustomerTotal, valuesToUse);

          setRateCustomerTotal(currentRateCustomerTotal);
        }

        setValues(valuesToUse);

        setTruckFleetVendor(truckFleetVendor);
        setPrimaryDriverDistanceToStart(primaryDistanceToStart);
        setPrimaryDriverPayedHours(R.pathOr(null, [GC.FIELD_PAYED_HOURS], driver));
        setSecondaryDriverPayedHours(R.pathOr(null, [GC.FIELD_PAYED_HOURS], secondaryDriver));
        setSecondaryDriverDistanceToStart(R.pathOr(0, [GC.FIELD_DISTANCE_TO_START], secondaryDriver));

        if (R.and(G.isNilOrEmpty(truckFleetVendor), R.equals(activeDriver, 'vendor'))) {
          setActiveDriver('primary');

          if (G.isNotNilAndNotEmpty(truckGuid)) setFieldValue(GC.FIELD_FLEET_VENDOR_CHARGES, null);
        }

        if (R.and(G.isNotNilAndNotEmpty(truckFleetVendor), G.notEquals(activeDriver, 'vendor'))) {
          setActiveDriver('vendor');
        }
      } else if (R.equals(GC.FIELD_SECONDARY_DRIVER_GUID, fieldId)) {
        if (R.isNil(driverGuid)) {
          const newValues = {
            ...values,
            [GC.FIELD_SECONDARY_DRIVER_GUID]: null,
            [GC.FIELD_SECONDARY_DRIVER_CHARGES]: null,
          };

          setValues(newValues);
        } else {
          const driverCharges = R.path([GC.FIELD_CHARGES], driver);

          if (R.isNil(driverCharges)) {
            const resData = await getTeamDriverCompensations(driverGuid, props);

            const charges = R.path(['rateData', 0, GC.FIELD_CHARGES], resData);

            const newValues = {
              ...values,
              [GC.FIELD_SECONDARY_DRIVER_GUID]: driverGuid,
              [GC.FIELD_SECONDARY_DRIVER_CHARGES]: getDriverInitCharges(charges, props),
            };

            setValues(newValues);
          } else {
            const newValues = {
              ...values,
              [GC.FIELD_SECONDARY_DRIVER_GUID]: driverGuid,
              [GC.FIELD_SECONDARY_DRIVER_CHARGES]: getDriverInitCharges(driverCharges, props),
            };

            setValues(newValues);
          }
        }

        setSecondaryDriverPayedHours(R.pathOr(0, [GC.FIELD_PAYED_HOURS], driver));
        setSecondaryDriverDistanceToStart(R.pathOr(0, [GC.FIELD_DISTANCE_TO_START], driver));
      }

      G.callFunction(handleSetRecalculateAllChargesId);
    },
  }),
  withHandlers({
    handleChangeSelectedRateGeneratedGuid: (props) => () => {
      const { values, isUpdate, handleChangeDriversSelect } = props;

      const driverGuid = R.path([GC.FIELD_PRIMARY_DRIVER_GUID], values);

      if (G.isNilOrEmpty(driverGuid)) return;

      handleChangeDriversSelect(driverGuid, GC.FIELD_PRIMARY_DRIVER_GUID, 'newCompensation', isUpdate);
    },
    handleChangeVendorRatesGeneratedGuid: (props) => () => {
      const { values, setValues, handleSetRecalculateAllChargesId } = props;

      const truckGuid = R.path([GC.FIELD_TRUCK_GUID], values);
      const availTrucks = R.pathOr([], ['availTrucks'], props);
      const truck = R.find(R.propEq(truckGuid, GC.FIELD_GUID), availTrucks);
      const truckFleetVendor = R.path([GC.SYSTEM_OBJECT_FLEET_VENDOR], truck);

      if (R.or(G.isNilOrEmpty(truckGuid), G.isNilOrEmpty(truckFleetVendor))) return;

      const result = makeResultObjectOnChangeVendorRate(props);

      const newValues = G.mergeWithoutOneFieldNullUndefinedOrEmptyString(GC.FIELD_CUSTOMER_TOTAL, values, result);

      setValues(newValues);

      G.callFunction(handleSetRecalculateAllChargesId);
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'rateGeneratedGuid',
    callbackName: 'handleChangeSelectedRateGeneratedGuid',
  }),
  withComponentDidUpdatePropCallback({
    propName: 'vendorRateGeneratedGuid',
    callbackName: 'handleChangeVendorRatesGeneratedGuid',
  }),
);

const enhanceFleetMap = compose(
  withHandlers({
    handleClickShowMap: (props) => () => {
      const {
        openModal,
        availTrucks,
        availTrailers,
        availableDriversData,
      } = props;

      const modalContent = (
        <RateFleetMap
          trucks={availTrucks}
          trailers={availTrailers}
          drivers={R.path(['drivers'], availableDriversData)}
          stops={R.pathOr([], [GC.FIELD_TEL, GC.FIELD_LOAD_STOPS], props)}
          selectedDriver={R.path(['values', GC.FIELD_PRIMARY_DRIVER_GUID], props)}
        />
      );

      const modal = {
        p: '0',
        component: modalContent,
        options: {
          withCloseIcon: true,
        },
      };

      openModal(modal);
    },
  }),
);

const getAvailableDriversHandler = ({ isFromOrder, isFromPlanner }) => {
  if (G.isTrue(isFromPlanner)) return handleGetAssignmentFromRouteBuilder;

  if (G.isTrue(isFromOrder)) return handleGetAssignmentFromOrder;

  return handleGetAssignmentNew;
};

const setFleetAssignmentFromValues = (values) => {
  const fleetAssignment = R.pick(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR_2, values);

  return R.assoc(GC.FIELD_FLEET_ASSIGNMENT, fleetAssignment, values);
};

const getDriverName = (guid, rateData, { drivers }) => {
  const driver = R.pathOr(
    R.find(R.propEq(guid, GC.FIELD_GUID), drivers),
    [guid],
    rateData,
  );

  const firstName = R.pathOr('', [GC.FIELD_FIRST_NAME], driver);
  const lastName = R.pathOr('', [GC.FIELD_LAST_NAME], driver);

  if (R.and(G.isNilOrEmpty(firstName), G.isNilOrEmpty(lastName))) return null;

  return `${firstName} ${lastName}`;
};

const getTruckName = (guid, trucks) => R.compose(
  R.prop(GC.FIELD_UNIT_ID),
  R.find(R.propEq(guid, GC.FIELD_GUID)),
)(trucks);

const getTrailerNames = (guids, trailers) => R.compose(
  R.map(R.prop(GC.FIELD_UNIT_ID)),
  R.values,
  R.pick(guids),
  R.indexBy(R.prop(GC.FIELD_GUID)),
)(trailers);

const setInfoToDriverRate = (
  values,
  rateData,
  { availTrucks, availTrailers, availableDriversData },
) => {
  const { guid, truckGuid, trailerGuids, primaryDriverGuid, secondaryDriverGuid } = values;

  return {
    ...values,
    isNew: G.isNilOrEmpty(guid),
    truckName: getTruckName(truckGuid, availTrucks),
    primaryDriverName: getDriverName(primaryDriverGuid, rateData, availableDriversData),
    secondaryDriverName: getDriverName(secondaryDriverGuid, rateData, availableDriversData),
    trailerNames: R.isNil(trailerGuids) ? [] : getTrailerNames((trailerGuids), availTrailers),
  };
};

const enhance = compose(
  withState('notAllowedCustomerRate', 'setNotAllowedCustomerRate', ({ notAllowCustomerRateInit }) => (
    R.or(notAllowCustomerRateInit, false)
  )),
  withHandlers({
    handleChangeDriver: ({ setActiveDriver }) => (value) => setActiveDriver(value),
    handleChangeTruckSelect: (props) => (truckGuid) => {
      const {
        isCreate,
        availTrucks,
        activeDriver,
        setFieldValue,
        initialValues,
        setActiveDriver,
        rateCustomerTotal,
        setTruckFleetVendor,
        setVendorRateCustomerTotal,
      } = props;

      const truck = R.find(R.propEq(truckGuid, GC.FIELD_GUID), availTrucks);
      const truckFleetVendor = G.getPropFromObject(GC.SYSTEM_OBJECT_FLEET_VENDOR, truck);

      if (G.isNilOrEmpty(truckFleetVendor)) {
        setTruckFleetVendor(null);
        setVendorRateCustomerTotal(null);
        setFieldValue(GC.FIELD_FLEET_VENDOR_CHARGES, null);

        if (isCreate) {
          const initialCustomerTotal = R.prop(GC.FIELD_CUSTOMER_TOTAL, initialValues);

          const customerTotal = G.ifElse(
            R.isNotNil(rateCustomerTotal),
            rateCustomerTotal,
            initialCustomerTotal,
          );

          setFieldValue(GC.FIELD_CUSTOMER_TOTAL, customerTotal);
        }

        if (R.equals(activeDriver, 'vendor')) {
          setActiveDriver('primary');
        }
      }

      if (G.isNotNilAndNotEmpty(truckFleetVendor)) {
        setTruckFleetVendor(truckFleetVendor);

        if (G.notEquals(activeDriver, 'vendor')) {
          setActiveDriver('vendor');
        }

        handleGetVendorRates(truckGuid, props);
      }
    },
    handleClickShowAllTrailers: (props) => () => {
      const {
        telGuid,
        isFromOrder,
        isFromPlanner,
        getAvailTrailersByTelGuid,
        getAvailTrailersByBranchGuidAndTelEvents,
      } = props;

      if (G.isAnyTrue(isFromOrder, isFromPlanner)) {
        const { loadData, telEvents } = props;

        const branchGuid = getTelBranchGuid(props);
        const events = isFromOrder ? G.getPropFromObject(GC.FIELD_LOAD_STOPS, loadData) : telEvents;

        if (G.isAllNotNilOrNotEmpty([events, branchGuid])) {
          getAvailTrailersByBranchGuidAndTelEvents({
            events,
            showAll: true,
            [GC.FIELD_BRANCH_GUID]: branchGuid,
          });
        }
      } else {
        getAvailTrailersByTelGuid({ telGuid, showAll: true });
      }
    },
    handleChangeMode: (props) => (e) => {
      const { values, setValues, isEditMode, sharedAccessorialsApplyTo } = props;

      const { fleetVendorCharges, primaryDriverCharges, secondaryDriverCharges } = values;

      const value = G.getEventTargetValue(e);

      const sharedApplyToFiltered = R.or(isEditMode, G.isNilOrEmpty(R.prop(GC.FIELD_MODE, values))) ?
        [] : G.filterChargesByMode(sharedAccessorialsApplyTo, value);

      setValues({
        ...values,
        [GC.FIELD_MODE]: value,
        [GC.FIELD_FLEET_VENDOR_CHARGES]: G.isNilOrEmpty(fleetVendorCharges) ?
          sharedApplyToFiltered :
          [...G.filterChargesByModeOrNoMode(fleetVendorCharges, value), ...sharedApplyToFiltered],
        [GC.FIELD_PRIMARY_DRIVER_CHARGES]: G.isNilOrEmpty(primaryDriverCharges) ?
          sharedApplyToFiltered :
          [...G.filterChargesByModeOrNoMode(primaryDriverCharges, value), ...sharedApplyToFiltered],
        [GC.FIELD_SECONDARY_DRIVER_CHARGES]: G.isNilOrEmpty(secondaryDriverCharges) ?
          sharedApplyToFiltered :
          [...G.filterChargesByModeOrNoMode(secondaryDriverCharges, value), ...sharedApplyToFiltered],
      });
    },
  }),
  withHandlers({
    handleCustomChange: (props) => (fieldValue, fieldId) => {
      const {
        values,
        setValues,
        ignoreHazmat,
        setFieldValue,
        setIgnoreHazmat,
        handleChangeMode,
        handleChangeTruckSelect,
        handleChangeDriversSelect,
      } = props;

      if (R.includes(fieldId, [GC.FIELD_PRIMARY_DRIVER_GUID, GC.FIELD_SECONDARY_DRIVER_GUID])) {
        handleChangeDriversSelect(fieldValue, fieldId);
      }

      if (R.equals(fieldId, GC.FIELD_TRUCK_GUID)) {
        setFieldValue(fieldId, fieldValue);
        handleChangeTruckSelect(fieldValue);
      }

      if (R.equals(fieldId, GC.FIELD_MODE)) {
        handleChangeMode(fieldValue);
      }

      if (R.equals(fieldId, 'ignoreHazmat')) {
        const value = R.not(ignoreHazmat);

        setValues({
          ...values,
          [fieldId]: value,
          [GC.FIELD_PRIMARY_DRIVER_GUID]: null,
          [GC.FIELD_SECONDARY_DRIVER_GUID]: null,
        });

        setIgnoreHazmat(value);
        getAvailableDriversHandler(props)({ ...props, ignoreHazmat: value });
      }
    },
  }),
  pure,
);

export const prepareCreateDriverRate = (formValues, shouldSelect) => {
  const values = G.isNilOrEmpty(G.getPropFromObject(GC.FIELD_SECONDARY_DRIVER_GUID, formValues))
    ? R.assoc(GC.FIELD_SECONDARY_DRIVER_CHARGES, null, formValues)
    : formValues;

  const chassisFields = R.pick(GC.CHASSIS_GROUPED_FIELDS, values);
  const fleetAssignment = R.mergeRight(R.propOr({}, GC.FIELD_FLEET_ASSIGNMENT, values), chassisFields);
  const valuesWithoutEmptyStrings = G.mapObjectEmptyStringFieldsToNull(values);

  const data = R.mergeRight(valuesWithoutEmptyStrings, {
    fleetAssignment,
    selected: G.ifElse(shouldSelect, true, false),
  });

  return G.omitEmptyChargesFromDriverRate(data);
};

const enhanceCreate = compose(
  withState('ignoreHazmat', 'setIgnoreHazmat', false),
  withRecalculateAllChargesId,
  withFleetAssignment,
  withAsyncMarginViolationRules,
  withPreselectedTrailers,
  withState('activeDriver', 'setActiveDriver', 'primary'),
  withFormik({
    enableReinitialize: true,
    mapPropsToValues: (props) => setDefaultDriverRateValues(props),
    validationSchema: () => Yup.lazy((values) => getDriverRateValidationSchema(values)),
    handleSubmit: (formValues, { props }) => {
      const {
        rateData,
        openModal,
        closeModal,
        isFromOrder,
        availTrucks,
        shouldSelect,
        isFromPlanner,
        availTrailers,
        handleSendTelRate,
        availableDriversData,
      } = props;

      const values = G.isNilOrEmpty(G.getPropFromObject(GC.FIELD_SECONDARY_DRIVER_GUID, formValues))
        ? R.assoc(GC.FIELD_SECONDARY_DRIVER_CHARGES, null, formValues)
        : formValues;
      const valuesWithoutEmptyStrings = G.mapObjectEmptyStringFieldsToNull(values);
      const dataToUse = prepareCreateDriverRate(formValues, shouldSelect);

      let submitAction = () => handleSendTelRate(dataToUse);

      if (isFromPlanner) {
        submitAction = () => handleSendTelRate(setInfoToDriverRate(
          dataToUse,
          rateData,
          { availTrucks, availTrailers, availableDriversData },
        ));
      }

      if (isFromOrder) {
        submitAction = () => handleSendTelRate(setFleetAssignmentFromValues(valuesWithoutEmptyStrings));
      }

      handleCheckChargesBeforeSubmit({ values, openModal, closeModal, submitAction });
    },
    displayName: 'ADD_DRIVER_RATE_FORM',
  }),
  withAsyncDeadheadMiles,
  withSearchLocation,
  enhanceDrivers,
  enhanceFleetMap,
  withProps(({ withHazardousItems, asyncBranchConfigs }) => ({
    shouldCheckHazmat: R.and(
      withHazardousItems,
      G.isFalse(
        G.getConfigValueFromStore(GC.TEL_FLEET_ASSIGNMENT_DISABLE_HAZMAT_ENDORSEMENT_CHECK, asyncBranchConfigs),
      ),
    ),
  })),
  withHandlers({
    handleClickTelRateAutoAssign: (props) => () => {
      const { handleChangeDriversSelect } = props;

      const assignAvailable = G.isTrue(R.pathOr(false, ['rateInfo', 'autoAssignAvailable'], props));

      if (assignAvailable) {
        const driverGuid = R.path(['rateInfo', 'forAssignDriverGuid'], props);

        if (G.isNotNilAndNotEmpty(driverGuid)) {
          handleChangeDriversSelect(driverGuid, GC.FIELD_PRIMARY_DRIVER_GUID);
        }
      }
    },
    handleChangeAsyncBranchConfigs: (props) => () => {
      const { setIgnoreHazmat, shouldCheckHazmat, asyncBranchConfigs } = props;

      if (R.isNil(asyncBranchConfigs)) return;

      const ignoreHazmat = R.not(shouldCheckHazmat);

      setIgnoreHazmat(ignoreHazmat);

      getAvailableDriversHandler(props)({ ...props, ignoreHazmat });
    },
    handleClickRefreshDrivers: (props) => async () => {
      const { values } = props;

      const { truckGuid } = values;

      if (G.isNilOrEmpty(truckGuid)) {
        getAvailableDriversHandler(props)(props, true);
      } else {
        await handleGetAllVendorRates(props);

        getAvailableDriversHandler(props)(props, true);
      }
    },
  }),
  withProps({
    isCreate: true,
    disablePointLocations: false,
  }),
  withComponentDidUpdatePropCallback({
    propName: 'asyncBranchConfigs',
    callbackName: 'handleChangeAsyncBranchConfigs',
  }),
  enhance,
  pure,
);

const handleUpdateDriverRate = (formValues, props) => {
  const {
    rateData,
    openModal,
    closeModal,
    availTrucks,
    isFromPlanner,
    availTrailers,
    handleSendTelRate,
    availableDriversData,
  } = props;

  const values = G.isNilOrEmpty(G.getPropFromObject(GC.FIELD_SECONDARY_DRIVER_GUID, formValues))
    ? R.assoc(GC.FIELD_SECONDARY_DRIVER_CHARGES, null, formValues)
    : formValues;

  const chassisFields = R.pick(GC.CHASSIS_GROUPED_FIELDS, values);
  const fleetAssignment = R.mergeRight(values.fleetAssignment, chassisFields);

  const data = R.mergeRight(
    values,
    { fleetAssignment, [GC.FIELD_STATUS]: upperCase(R.prop(GC.FIELD_STATUS, values)) },
  );

  const dataWithoutEmptyStrings = G.mapObjectEmptyStringFieldsToNull(data);
  const dataToUse = G.omitEmptyChargesFromDriverRate(dataWithoutEmptyStrings);

  const fieldsToOmit = [
    GC.FIELD_MAX_PAY_VIOLATED,
    GC.FIELD_MIN_MARGIN_VIOLATED,
    GC.FIELD_CRITICAL_MARGIN_VIOLATED,
  ];

  let submitAction = () => handleSendTelRate(R.omit(fieldsToOmit, dataToUse));

  if (isFromPlanner) {
    submitAction = () => handleSendTelRate(setInfoToDriverRate(
      R.omit(fieldsToOmit, dataToUse),
      rateData,
      { availTrucks, availTrailers, availableDriversData },
    ));
  }

  handleCheckChargesBeforeSubmit({ values, openModal, closeModal, submitAction });
};

const enhanceUpdate = compose(
  withProps({
    isUpdate: true,
    isEditMode: true,
    disablePointLocations: true,
  }),
  withRecalculateAllChargesId,
  withFleetAssignment,
  withAsyncMarginViolationRules,
  withState('activeDriver', 'setActiveDriver', 'primary'),
  withFormik({
    enableReinitialize: true,
    displayName: 'EDIT_DRIVER_RATE_FORM',
    mapPropsToValues: (props) => setDefaultDriverRateValues(props),
    handleSubmit: (values, { props }) => handleUpdateDriverRate(values, props),
    validationSchema: () => Yup.lazy((values) => getDriverRateValidationSchema(values)),
  }),
  withHandlers({
    handleGetSharedAccessorialsApplyTo: (props) => async () => {
      const {
        items,
        values,
        telGuid,
        loadData,
        setValues,
        branchGuid,
        accessorials,
        matrixAccessorials,
        handleSetRecalculateAllChargesId,
      } = props;

      const sharedApplyTo = await getTelSharedAccessorialsApplyTo({
        items,
        telGuid,
        loadData,
        branchGuid,
        matrixAccessorials,
        accessorials: R.values(accessorials),
        stopCount: R.pathOr(0, ['rateInfo', 'stopCount'], props),
        applyToType: GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_FLEET,
      });

      const sharedApplyToFiltered = G.filterChargesByModeOrNoMode(sharedApplyTo, R.prop(GC.FIELD_MODE, values));

      const chargesArrayName = getChargesArrayNameFromProps(props);
      const rateCharges = R.pathOr([], [chargesArrayName], values);
      const charges = G.replaceAutoChargesWithNewSharedAssessorialsApplyTo(rateCharges, sharedApplyToFiltered);
      const newValues = R.assoc(chargesArrayName, charges, values);

      setValues(newValues);

      G.callFunction(handleSetRecalculateAllChargesId);
    },
  }),
  withAsyncDeadheadMiles,
  withSearchLocation,
  enhanceDrivers,
  enhance,
  lifecycle({
    componentDidMount() {
      const props = this.props;
      getAvailableDriversHandler(props)(props);
    },
  }),
  withHandlers({
    handleClickRefreshDrivers: (props) => async () => {
      const { values: { primaryDriverGuid } } = props;

      handleGetSingleDriverCompensation(primaryDriverGuid, props);
    },
  }),
  pure,
);

const getCommonChargeProps = (props) => {
  const currency = R.pathOr(
    R.pathOr(0, ['orderRateInfo', 'currency'], props),
    ['values', GC.FIELD_CURRENCY],
    props,
  );

  const customerLineHaul = R.pathOr(
    R.pathOr(0, ['orderRateInfo', 'customerLineHaul'], props),
    ['rateInfo', 'customerLineHaul'],
    props,
  );

  const totalCustomersRate = R.pathOr(
    R.pathOr(0, ['orderRateInfo', GC.FIELD_TOTAL_CUSTOMER_RATE], props),
    ['rateInfo', GC.FIELD_TOTAL_CUSTOMER_RATE],
    props,
  );

  const stopsQuantity = R.pathOr(0, ['rateInfo', 'stopCount'], props);
  const totalQuantities = R.path(['rateInfo', 'totalQuantities'], props);
  const cloRateAdjustments = R.pathOr([], ['rateInfo', 'cloRateAdjustments'], props);
  const normalizedTotal = R.pathOr({}, ['values', GC.SYSTEM_OBJECT_NORMALIZED_TOTAL], props);

  return {
    currency,
    stopsQuantity,
    normalizedTotal,
    totalQuantities,
    customerLineHaul,
    totalCustomersRate,
    cloRateAdjustments,
  };
};

const DriverCharges = (props) => {
  const {
    eventDates,
    isEditMode,
    branchGuid,
    accessorials,
    activeDriver,
    recalculateAllChargesId,
    primaryDriverPayedHours,
    secondaryDriverPayedHours,
    primaryDriverDistanceToStart,
    secondaryDriverDistanceToStart,
  } = props;

  const commonTotalProps = G.getTotalsFromValues(props);
  const commonChargeProps = getCommonChargeProps(props);

  const accessorialType = R.equals(activeDriver, 'vendor')
    ? GC.ACCESSORIAL_TYPE_VENDOR_RATE_INVOICE : GC.ACCESSORIAL_TYPE_DRIVER_RATE_PAYROLL;

  const accessorialOptions = useMemo(() => getAccessorialsForState(R.values(accessorials || {}), GC.LOAD_TYPE_TEL, {
    grouped: true,
    accessorialType,
    includeContract: false,
  }), [accessorials, accessorialType]);

  const activeChargeProps = {
    primary: {
      eventDates,
      isDriverRate: true,
      payedHours: primaryDriverPayedHours,
      distanceToStart: primaryDriverDistanceToStart,
      chargesArrayName: GC.FIELD_PRIMARY_DRIVER_CHARGES,
    },
    team: {
      isDriverRate: true,
      payedHours: secondaryDriverPayedHours,
      distanceToStart: secondaryDriverDistanceToStart,
      chargesArrayName: GC.FIELD_SECONDARY_DRIVER_CHARGES,
    },
    vendor: {
      isVendor: true,
      isVendorRate: true,
      activeTab: activeDriver,
      chargesArrayName: GC.FIELD_FLEET_VENDOR_CHARGES,
    },
  }[activeDriver];

  return (
    <div>
      {
        activeChargeProps &&
        <DMChargeComponent
          {...commonTotalProps}
          {...commonChargeProps}
          {...activeChargeProps}
          loadType={GC.FIELD_TEL}
          isEditMode={isEditMode}
          branchGuid={branchGuid}
          rateProps={{ ...props, ...accessorialOptions }}
          recalculateAllChargesId={recalculateAllChargesId}
        />
      }
    </div>
  );
};

const DriverRateForm = (props) => {
  const {
    tel,
    closeModal,
    handleSubmit,
    asyncConfigs,
    handleCustomChange,
    notAllowedCustomerRate,
    asyncUserGeneralListFullName,
    values: { primaryDriverGuid },
  } = props;

  const { marginViolated, maxPayViolated, criticalMarginViolated } = getMarginViolationData(props);

  const vendorGuid = primaryDriverGuid ? R.compose(
    R.prop('vendorGuid'),
    R.find(R.propEq(primaryDriverGuid, 'value')),
    R.pathOr([], ['fleetOptionsForSelect', 'primaryDriversOptions']),
  )(props) : null;

  const optionForSelect = {
    ...sortTruckOptions(filterDriversOptions(props), vendorGuid),
    dispatchedByOptions: mapDispatchersToOptions(asyncUserGeneralListFullName),
  };

  const eventDates = G.getFirstLastEventDateFromLoad(tel, GC.LOAD_TYPE_TEL, asyncConfigs);

  return (
    <Box width='950px' bg='white'>
      <RateHeader {...props} mode='create' />
      <form onSubmit={handleSubmit}>
        <MarginViolationSection
          {...G.getFormikProps(props)}
          marginViolated={marginViolated}
          maxPayViolated={maxPayViolated}
          criticalMarginViolated={criticalMarginViolated}
          optionsForSelect={{
            marginViolationReasonOptions: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
              asyncConfigs,
              GC.RATE_ENGINE_MARGIN_VIOLATION_REASON,
              true,
            ),
          }}
        />
        <DriverSection {...props} optionsForSelect={optionForSelect} />
        <SectionsDivider />
        {G.getConfigDriverRateShowStartFinishPointFromWindow() && <PointsSection {...props} />}
        {G.getConfigDriverRateShowStartFinishPointFromWindow() && <SectionsDivider />}
        <GeneralSectionForTel
          {...G.getFormikPropsToFieldset(props)}
          handleCustomChange={handleCustomChange}
          optionsForSelect={G.getModeServiceFromAsyncConfigsForRateSelect(props)}
        />
        <DriverCharges {...props} eventDates={eventDates} />
        <StickedBox bottom='0px' zIndex={13}>
          <ChargesTotal {...props} charges={getChargesFromValues(props)} />
          <ChargeFormFooter closeModal={closeModal} submitDisabled={notAllowedCustomerRate} />
          <NotAllowedCustomerRate notAllowedCustomerRate={notAllowedCustomerRate} />
        </StickedBox>
      </form>
    </Box>
  );
};

const EditDriverRate = (props) => {
  const {
    tel,
    width,
    values,
    handleSubmit,
    asyncConfigs,
    initialValues,
    submitBtnStyles,
    cancelBtnStyles,
    handleCustomChange,
    notAllowedCustomerRate,
    asyncUserGeneralListFullName,
    values: { primaryDriverGuid },
  } = props;

  const { marginViolated, maxPayViolated, criticalMarginViolated } = getMarginViolationData(props);

  const vendorGuid = primaryDriverGuid ? R.compose(
    R.prop('vendorGuid'),
    R.find(R.propEq(primaryDriverGuid, 'value')),
    R.pathOr([], ['fleetOptionsForSelect', 'primaryDriversOptions']),
  )(props) : null;

  const fleetOptionsWithFilteredDrivers = sortTruckOptions(filterDriversOptions(props), vendorGuid);

  const optionForSelect = {
    ...getDeletedDriversOptions(initialValues, fleetOptionsWithFilteredDrivers),
    dispatchedByOptions: mapDispatchersToOptions(asyncUserGeneralListFullName),
  };

  const eventDates = G.getFirstLastEventDateFromLoad(tel, GC.LOAD_TYPE_TEL, asyncConfigs);

  return (
    <Box bg='white' width={R.or(width, 950)}>
      <RateHeader {...props} mode='edit' />
      <form onSubmit={handleSubmit}>
        <MarginViolationSection
          {...G.getFormikProps(props)}
          marginViolated={marginViolated}
          maxPayViolated={maxPayViolated}
          criticalMarginViolated={criticalMarginViolated}
          optionsForSelect={{
            marginViolationReasonOptions: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
              asyncConfigs,
              GC.RATE_ENGINE_MARGIN_VIOLATION_REASON,
              true,
            ),
          }}
        />
        <EditDriverSection {...props} optionsForSelect={optionForSelect} />
        <SectionsDivider />
        {G.getConfigDriverRateShowStartFinishPointFromWindow() && <PointsSection {...props} />}
        {G.getConfigDriverRateShowStartFinishPointFromWindow() && <SectionsDivider />}
        <GeneralSectionForTel
          {...G.getFormikPropsToFieldset(props)}
          handleCustomChange={handleCustomChange}
          optionsForSelect={G.getModeServiceFromAsyncConfigsForRateSelect(props)}
        />
        <PriceSheetItems values={values} additionalFormHeaderSectionStyles={{ my: 10 }} />
        <DriverCharges {...props} eventDates={eventDates} />
        <StickedBox bottom='0px' zIndex={13}>
          <ChargesTotal {...props} charges={getChargesFromValues(props)} />
          <ChargeFormFooter
            submitBtnStyles={submitBtnStyles}
            cancelBtnStyles={cancelBtnStyles}
            submitDisabled={notAllowedCustomerRate}
          />
          <NotAllowedCustomerRate notAllowedCustomerRate={notAllowedCustomerRate} />
        </StickedBox>
      </form>
    </Box>
  );
};

const branchConfigsArray = [
  GC.TRAILER_MANAGE_TRAILERS_FROM,
  GC.TEL_DRIVER_RATE_TRUCK_REQUIRED,
  GC.TEL_DRIVER_RATE_TRAILER_REQUIRED,
  GC.TEL_DRIVER_RATE_START_POINT_REQUIRED,
  GC.TEL_DRIVER_RATE_FINISH_POINT_REQUIRED,
  GC.TEL_DRIVER_RATE_SHOW_START_FINISH_POINT,
  GC.TEL_FLEET_ASSIGNMENT_DISABLE_HAZMAT_ENDORSEMENT_CHECK,
];

const enhanceCreateWithConfigs = compose(
  withConnectModalAndLoaderActions,
  withAsyncGetUserGeneralListFullNameOnDidMount,
  withAsyncBranchConfigsByNames({ omitReceived: true, configs: branchConfigsArray }),
  withAsyncConfigs,
  withAsyncChargeAccessorials({ isDriver: true, isCreateMode: true }),
  enhanceCreate,
);

const enhanceUpdateWithConfigs = compose(
  withConnectModalAndLoaderActions,
  withAsyncGetUserGeneralListFullNameOnDidMount,
  withAsyncBranchConfigsByNames({ omitReceived: true, configs: branchConfigsArray }),
  withAsyncConfigs,
  withAsyncChargeAccessorials({ isDriver: true, isCreateMode: false }),
  enhanceUpdate,
);

export default enhanceCreateWithConfigs(DriverRateForm);

const EditDriverRateForm = enhanceUpdateWithConfigs(EditDriverRate);

export {
  DriverRateForm,
  EditDriverRate,
  EditDriverRateForm,
  handleUpdateDriverRate,
  enhanceCreateWithConfigs,
  enhanceUpdateWithConfigs,
};
